import { pick } from "lodash-es";
import { ensureExhaustive } from "@dhau/lang-extras";
import { sentryLogExtra } from "~/utils/sentry.ts";
import type { SetupConfig } from "./config.ts";
import EnterCode from "./enter-code/enter-code.tsx";
import EnterNumberOfKits from "./enter-number-of-kits/enter-number-of-kits.tsx";
import UploadImage from "./upload/upload-image.tsx";
import ImageConfirm from "./image-confirm/image-confirm.tsx";
import rotateSourceImage from "@brickme/frontend-common/src/source-image/rotate-source-image.ts";

type EnterCodeStep = {
	readonly type: "enter-code";
};

type NumberOfKitsStep = {
	readonly type: "number-of-kits";
	readonly progress: Pick<SetupConfig, "code">;
};

type UploadImageStep = {
	readonly type: "upload-image";
	readonly progress: Pick<SetupConfig, "code" | "numberOfKits">;
};

type ImageConfirmStep = {
	readonly type: "image-confirm";
	readonly progress: Pick<SetupConfig, "code" | "numberOfKits" | "sourceImage">;
};

type SetupFullStep =
	| EnterCodeStep
	| NumberOfKitsStep
	| UploadImageStep
	| ImageConfirmStep;

type SetupFullProps = {
	readonly step: SetupFullStep;
	readonly onStepChange: (step: SetupFullStep) => void;
	readonly onComplete: (config: SetupConfig, step: ImageConfirmStep) => void;
	readonly onCancel: () => void;
};

function SetupFull({
	step,
	onStepChange,
	onComplete,
	onCancel,
}: SetupFullProps) {
	switch (step.type) {
		case "enter-code":
			return (
				<EnterCode
					onComplete={(code) => {
						onStepChange({ type: "number-of-kits", progress: { code } });
						sentryLogExtra.setCode(code);
					}}
					onCancel={onCancel}
				/>
			);
		case "number-of-kits":
			return (
				<EnterNumberOfKits
					onComplete={(numberOfKits) => {
						onStepChange({
							type: "upload-image",
							progress: { ...step.progress, numberOfKits },
						});
						sentryLogExtra.setNumberOfKits(numberOfKits);
					}}
					onCancel={() => onStepChange({ type: "enter-code" })}
				/>
			);
		case "upload-image":
			return (
				<UploadImage
					onComplete={(sourceImage) => {
						onStepChange({
							type: "image-confirm",
							progress: { ...step.progress, sourceImage },
						});
						sentryLogExtra.setSourceImage(sourceImage);
					}}
					onCancel={() =>
						onStepChange({
							type: "number-of-kits",
							progress: pick(step.progress, "code"),
						})
					}
				/>
			);
		case "image-confirm":
			return (
				<ImageConfirm
					sourceImage={step.progress.sourceImage}
					onComplete={async (rotation) => {
						onComplete(
							{
								...step.progress,
								sourceImage: await rotateSourceImage(
									step.progress.sourceImage,
									rotation,
								),
							},
							step,
						);
						sentryLogExtra.setSourceImageConfirmed(
							step.progress.sourceImage,
							rotation,
						);
					}}
					onCancel={() =>
						onStepChange({
							type: "upload-image",
							progress: pick(step.progress, "code", "numberOfKits"),
						})
					}
				/>
			);
		default:
			ensureExhaustive(step);
	}
}

export type { SetupFullStep };
export default SetupFull;
